import { useContext, useMemo } from 'react'
import type {
  DesignConnectorShape,
  DesignShape,
} from '../../types/design'
import {
  arrowMarkerId,
  blendModeCss,
  buildConnectorPath,
  dasharray,
  fillToPaint,
  shapePathD,
  shapePathPoints,
} from './shapes'
import { ICON_BY_NAME } from './iconLibrary'
import { WorkspaceContext } from '../../context/workspaceContextCore'
import { useNls } from '../../nls/nlsProvider'
import { ChartPlacementEmbed } from '../Common/Chart/ChartPlacementEmbed'
import { ChartEmbed } from '../Common/Chart/ChartEmbed'

type Props = {
  shape: DesignShape
  shapesById?: Map<string, DesignShape>
  resolved?: { title?: string; color?: string | null; progress?: number } | null
  onTextChange?: (text: string) => void
  editing?: boolean
}

const Effects = ({ shape, id }: { shape: DesignShape; id: string }) => {
  if (!shape.effects || shape.effects.length === 0) return null
  return (
    <filter id={id} x="-30%" y="-30%" width="160%" height="160%">
      {shape.effects.map((e) => {
        if (e.effect === 'SHADOW') {
          if (e.kind === 'INNER') {
            return (
              <feGaussianBlur key={e.id} in="SourceAlpha" stdDeviation={e.blur / 2} result={`blur-${e.id}`} />
            )
          }
          return (
            <feDropShadow key={e.id} dx={e.x} dy={e.y} stdDeviation={e.blur / 2} floodColor={e.color} />
          )
        }
        if (e.effect === 'BLUR') {
          return <feGaussianBlur key={e.id} stdDeviation={e.radius} />
        }
        return null
      })}
    </filter>
  )
}

const FillDef = ({ shape, id }: { shape: DesignShape; id: string }) => {
  if (shape.fill.kind !== 'GRADIENT') return null
  const g = shape.fill.gradient
  if (g.kind === 'LINEAR') {
    const rad = (g.angle * Math.PI) / 180
    return (
      <linearGradient id={id} gradientUnits="objectBoundingBox" x1={0.5 - Math.cos(rad) / 2} y1={0.5 - Math.sin(rad) / 2} x2={0.5 + Math.cos(rad) / 2} y2={0.5 + Math.sin(rad) / 2}>
        {g.stops.map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />)}
      </linearGradient>
    )
  }
  return (
    <radialGradient id={id} cx="0.5" cy="0.5" r="0.5">
      {g.stops.map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />)}
    </radialGradient>
  )
}

const TextOverlay = ({ shape, editing, onTextChange, resolved }: { shape: DesignShape; editing?: boolean; onTextChange?: (t: string) => void; resolved?: Props['resolved'] }) => {
  if (shape.type === 'CONNECTOR' || shape.type === 'GROUP' || shape.type === 'FREEHAND') return null
  if (shape.type === 'POLYLINE' || shape.type === 'CURVE') return null
  if (shape.type === 'IMAGE' || shape.type === 'ICON' || shape.type === 'LINE' || shape.type === 'ARROW') return null
  const text = resolved?.title ?? shape.text ?? ''
  if (!text && !editing && shape.type !== 'TEXT' && shape.type !== 'STICKY') return null
  const isEmpty = !text && !editing && (shape.type === 'TEXT' || shape.type === 'STICKY')
  const style = shape.textStyle
  const fontSize = style?.fontSize ?? 14
  const fontWeight = style?.fontWeight ?? 600
  const color = style?.color ?? '#f8fafc' // mosaic-keep: user data
  const align = style?.align ?? 'CENTER'
  const padding = shape.type === 'STICKY' ? 14 : 8
  const justify = align === 'LEFT' ? 'flex-start' : align === 'RIGHT' ? 'flex-end' : 'center'
  return (
    <foreignObject x={shape.position.x + padding} y={shape.position.y + padding} width={Math.max(1, shape.size.width - padding * 2)} height={Math.max(1, shape.size.height - padding * 2)} pointerEvents={editing ? 'auto' : 'none'}>
      <div
        data-text-shape-id={shape.id}
        contentEditable={editing}
        suppressContentEditableWarning
        onBlur={(e) => onTextChange?.(e.currentTarget.innerText)}
        style={{
          width: '100%',
          height: '100%',
          color: isEmpty ? 'rgba(148,163,184,0.65)' : color, // mosaic-keep: user data
          fontFamily: style?.fontFamily ?? 'Inter, system-ui, sans-serif',
          fontSize,
          fontWeight,
          lineHeight: style?.lineHeight ?? 1.35,
          letterSpacing: style?.letterSpacing ?? 0,
          textAlign: align.toLowerCase() as 'left' | 'center' | 'right',
          display: 'flex',
          alignItems: shape.type === 'STICKY' ? 'flex-start' : 'center',
          justifyContent: justify,
          outline: 'none',
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
          wordBreak: 'break-word',
          fontStyle: isEmpty ? 'italic' : undefined,
        }}
      >
        {text || (isEmpty ? 'Double-click to edit' : '')}
      </div>
    </foreignObject>
  )
}

export const ShapeRenderer = ({ shape, shapesById, resolved, onTextChange, editing }: Props) => {
  const workspaceContext = useContext(WorkspaceContext)
  const workspaceId = workspaceContext?.activeWorkspaceId ?? ''
  const { t } = useNls()
  const idBase = `s-${shape.id}`
  const filterId = `${idBase}-filter`
  const fillId = `${idBase}-fill`
  const opacity = shape.opacity ?? 1
  const blend = shape.blendMode && shape.blendMode !== 'NORMAL' ? blendModeCss(shape.blendMode) : undefined
  const transform = shape.rotation ? `rotate(${shape.rotation} ${shape.position.x + shape.size.width / 2} ${shape.position.y + shape.size.height / 2})` : undefined

  const strokeProps = useMemo(() => shape.stroke ? {
    stroke: shape.stroke.color,
    strokeWidth: shape.stroke.width,
    strokeDasharray: dasharray(shape.stroke),
  } : { stroke: 'none' }, [shape.stroke])

  const paintFill = useMemo(() => {
    if (resolved?.color) return resolved.color
    return fillToPaint(shape.fill, fillId)
  }, [shape.fill, fillId, resolved])

  const wrapperStyle: React.CSSProperties = {
    opacity,
    mixBlendMode: blend as React.CSSProperties['mixBlendMode'],
  }

  const defs = (
    <defs>
      <FillDef shape={shape} id={fillId} />
      <Effects shape={shape} id={filterId} />
    </defs>
  )
  const filterAttr = shape.effects && shape.effects.length > 0 ? `url(#${filterId})` : undefined

  const renderProgress = () => {
    if (resolved?.progress === undefined || resolved?.progress === null) return null
    const p = Math.max(0, Math.min(100, resolved.progress))
    const barH = 4
    const barW = Math.max(40, shape.size.width - 24)
    const barX = shape.position.x + (shape.size.width - barW) / 2
    const barY = shape.position.y + shape.size.height - barH - 8
    return (
      <g pointerEvents="none">
        <rect x={barX} y={barY} width={barW} height={barH} rx={2} fill="rgba(15,23,42,0.55)" />{/* mosaic-keep: user data */}
        <rect x={barX} y={barY} width={(barW * p) / 100} height={barH} rx={2} fill="url(#progressGrad)" />
      </g>
    )
  }

  if (shape.type === 'CONNECTOR') {
    const path = shapesById ? buildConnectorPath(shape as DesignConnectorShape, shapesById) : null
    if (!path) return null
    const color = shape.stroke?.color ?? '#94a3b8' // mosaic-keep: user data
    const wid = shape.stroke?.width ?? 2
    const markerEnd = shape.arrowEnd !== 'NONE' ? `url(#${arrowMarkerId('end', shape.arrowEnd, color)})` : undefined
    const markerStart = shape.arrowStart !== 'NONE' ? `url(#${arrowMarkerId('start', shape.arrowStart, color)})` : undefined
    return (
      <g style={wrapperStyle} transform={transform}>
        <path d={path.d} fill="none" stroke={color} strokeWidth={wid} strokeDasharray={dasharray(shape.stroke)} strokeLinecap="round" strokeLinejoin="round" markerEnd={markerEnd} markerStart={markerStart} />
      </g>
    )
  }

  const box = { x: shape.position.x, y: shape.position.y, width: shape.size.width, height: shape.size.height }
  let primary: React.ReactNode = null

  switch (shape.type) {
    case 'RECT':
    case 'ROUNDED_RECT':
    case 'PROCESS':
      primary = <rect x={box.x} y={box.y} width={box.width} height={box.height} rx={('cornerRadius' in shape ? shape.cornerRadius : 0) ?? 0} fill={paintFill} {...strokeProps} filter={filterAttr} />
      break
    case 'ELLIPSE':
    case 'BUBBLE':
      primary = <ellipse cx={box.x + box.width / 2} cy={box.y + box.height / 2} rx={box.width / 2} ry={box.height / 2} fill={paintFill} {...strokeProps} filter={filterAttr} />
      break
    case 'TRIANGLE':
    case 'DIAMOND':
    case 'PARALLELOGRAM':
    case 'HEXAGON':
      primary = <polygon points={shapePathPoints(shape.type, box)} fill={paintFill} {...strokeProps} filter={filterAttr} />
      break
    case 'CYLINDER':
    case 'DOCUMENT':
    case 'TERMINATOR':
    case 'OFF_PAGE':
      primary = <path d={shapePathD(shape.type, box)} fill={paintFill} {...strokeProps} filter={filterAttr} />
      break
    case 'POLYGON':
    case 'STAR': {
      const opts = { sides: 'sides' in shape ? shape.sides : 6, innerRatio: 'innerRatio' in shape ? shape.innerRatio : 0.5 }
      primary = <path d={shapePathD(shape.type, box, opts)} fill={paintFill} {...strokeProps} filter={filterAttr} />
      break
    }
    case 'LINE':
    case 'ARROW': {
      const color = shape.stroke?.color ?? '#cbd5e1' // mosaic-keep: user data
      const wid = shape.stroke?.width ?? 2
      const markerEnd = shape.type === 'ARROW' && shape.arrowEnd !== 'NONE' ? `url(#${arrowMarkerId('end', shape.arrowEnd ?? 'TRIANGLE', color)})` : shape.arrowEnd && shape.arrowEnd !== 'NONE' ? `url(#${arrowMarkerId('end', shape.arrowEnd, color)})` : undefined
      const markerStart = shape.arrowStart && shape.arrowStart !== 'NONE' ? `url(#${arrowMarkerId('start', shape.arrowStart, color)})` : undefined
      // Prefer path.p1/p2 (any-angle); fall back to horizontal-through-AABB for legacy assets.
      const p1 = shape.path ? shape.path.p1 : { x: box.x, y: box.y + box.height / 2 }
      const p2 = shape.path ? shape.path.p2 : { x: box.x + box.width, y: box.y + box.height / 2 }
      primary = (
        <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={color} strokeWidth={wid} strokeDasharray={dasharray(shape.stroke)} markerEnd={markerEnd} markerStart={markerStart} strokeLinecap="round" />
      )
      break
    }
    case 'POLYLINE': {
      const color = shape.stroke?.color ?? '#cbd5e1' // mosaic-keep: user data
      const wid = shape.stroke?.width ?? 2
      const markerEnd = shape.arrowEnd && shape.arrowEnd !== 'NONE' ? `url(#${arrowMarkerId('end', shape.arrowEnd, color)})` : undefined
      const markerStart = shape.arrowStart && shape.arrowStart !== 'NONE' ? `url(#${arrowMarkerId('start', shape.arrowStart, color)})` : undefined
      const points = shape.points.map((p) => `${p.x},${p.y}`).join(' ')
      primary = (
        <polyline points={points} fill="none" stroke={color} strokeWidth={wid} strokeDasharray={dasharray(shape.stroke)} strokeLinecap="round" strokeLinejoin="round" markerEnd={markerEnd} markerStart={markerStart} />
      )
      break
    }
    case 'CURVE': {
      const color = shape.stroke?.color ?? '#cbd5e1' // mosaic-keep: user data
      const wid = shape.stroke?.width ?? 2
      const markerEnd = shape.arrowEnd && shape.arrowEnd !== 'NONE' ? `url(#${arrowMarkerId('end', shape.arrowEnd, color)})` : undefined
      const markerStart = shape.arrowStart && shape.arrowStart !== 'NONE' ? `url(#${arrowMarkerId('start', shape.arrowStart, color)})` : undefined
      const d = `M ${shape.p1.x} ${shape.p1.y} C ${shape.c1.x} ${shape.c1.y}, ${shape.c2.x} ${shape.c2.y}, ${shape.p2.x} ${shape.p2.y}`
      primary = (
        <path d={d} fill="none" stroke={color} strokeWidth={wid} strokeDasharray={dasharray(shape.stroke)} strokeLinecap="round" strokeLinejoin="round" markerEnd={markerEnd} markerStart={markerStart} />
      )
      break
    }
    case 'TEXT':
      primary = null
      break
    case 'STICKY': {
      const stickyColor = shape.fill.kind === 'SOLID' ? shape.fill.color : '#fde68a' // mosaic-keep: user data
      primary = (
        <g filter={filterAttr}>
          <rect x={box.x} y={box.y} width={box.width} height={box.height} rx={4} fill={stickyColor} />
          <path d={`M${box.x} ${box.y + box.height - 12} L${box.x + 12} ${box.y + box.height} L${box.x} ${box.y + box.height} Z`} fill="rgba(0,0,0,0.15)" />{/* mosaic-keep: user data */}
        </g>
      )
      break
    }
    case 'FREEHAND': {
      if (!shape.points || shape.points.length < 2) {
        primary = null
        break
      }
      const d = shape.points
        .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`)
        .join(' ')
      primary = (
        <path d={d} fill="none" stroke={shape.stroke?.color ?? '#f8fafc' /* mosaic-keep: user data */} strokeWidth={shape.stroke?.width ?? 3} strokeLinecap="round" strokeLinejoin="round" filter={filterAttr} />
      )
      break
    }
    case 'IMAGE':
      primary = shape.src ? <image href={shape.src} x={box.x} y={box.y} width={box.width} height={box.height} preserveAspectRatio="xMidYMid slice" filter={filterAttr} /> : <rect x={box.x} y={box.y} width={box.width} height={box.height} fill="rgba(15,23,42,0.5)" stroke="rgba(148,163,184,0.4)" strokeDasharray="4 4" /> // mosaic-keep: user data
      break
    case 'CHART': {
      primary = (
        <foreignObject
          data-chart-shape="true"
          x={box.x}
          y={box.y}
          width={box.width}
          height={box.height}
          pointerEvents={editing ? 'auto' : 'none'}
        >
          <div className="designChartContainer" style={{ pointerEvents: editing ? 'auto' : 'none' }}>
            {shape.chartId ? (
              <ChartPlacementEmbed
                placement={{
                  chartId: shape.chartId,
                  overrides: { height: 'M' },
                }}
                workspaceId={workspaceId}
              />
            ) : (
              <ChartEmbed status="deleted">
                <div className="designChartPlaceholder">
                  {t('NLSDesignChartDeletedOrUnreadable')}
                </div>
              </ChartEmbed>
            )}
          </div>
        </foreignObject>
      )
      break
    }
    case 'ICON': {
      const IconComp = ICON_BY_NAME.get(shape.iconName)
      primary = (
        <foreignObject x={box.x} y={box.y} width={box.width} height={box.height} pointerEvents="none">
          <div style={{ width: '100%', height: '100%', color: shape.iconColor, display: 'grid', placeItems: 'center' }}>
            {IconComp ? <IconComp style={{ width: '100%', height: '100%' }} /> : null}
          </div>
        </foreignObject>
      )
      break
    }
    case 'GROUP':
      primary = null
      break
  }

  return (
    <g style={wrapperStyle} transform={transform} data-shape-id={shape.id} data-shape-type={shape.type}>
      {defs}
      {primary}
      <TextOverlay shape={shape} editing={editing} onTextChange={onTextChange} resolved={resolved} />
      {renderProgress()}
    </g>
  )
}
